import { liveDefinitions, LIVE_LIMITS } from './live-graph-modules.js';
import { validatePatchGraph, type PatchEndpoint, type PatchGraphV1, type PatchNodeV1 } from '../../packages/routing-graph/src/index.js';
import type { DspModuleDefinition, DspModuleInstance } from '../../packages/dsp-module-contract/src/index.js';
export { liveDefinitions, LIVE_LIMITS };
export interface LivePlan { readonly graph:PatchGraphV1; readonly outputs:readonly PatchEndpoint[]; readonly sampleRate:number; }
export type LiveCapacity=Readonly<Record<string,number>>;
type MutableBlock={frames:number;inputs:Record<string,Float32Array>;controls:Record<string,Float32Array>;outputs:Record<string,Float32Array>};
interface Slot {
 readonly definition:DspModuleDefinition; readonly instance:DspModuleInstance;
 id:string; parameters:Readonly<Record<string,number>>;
 block:MutableBlock; readonly outputBuffers:Float32Array[]; readonly silence:Float32Array;
}
interface Binding {readonly slot:Slot;readonly node:PatchNodeV1;readonly retained:boolean;readonly block:MutableBlock;}
export interface PreparedLivePlan {readonly owner:LiveGraphEngine;readonly generation:number;readonly bindings:readonly Binding[];readonly active:Slot[];readonly taps:Float32Array[];}
const definitions=new Map(liveDefinitions.map(d=>[d.descriptor.id,d]));
/** Pure admission adapter for the audited L1 setter domains. No live setter is a validator.
 * Discrete domains are explicit in descriptors; these setters have no cross-parameter
 * rejection. MOD023/025 are intentionally NOT admitted by this adapter.
 */
export function normalizedNode(node:PatchNodeV1,definition:DspModuleDefinition,sr:number):PatchNodeV1 {
 if(!definitions.has(node.moduleId)||node.moduleId!==definition.descriptor.id)throw new RangeError('unsupported live module');
 for(const key of Object.keys(node.parameters))if(!definition.descriptor.parameters.some(p=>p.id===key))throw new RangeError(`unknown parameter ${node.id}.${key}`);
 const parameters:Record<string,number>={};
 for(const p of definition.descriptor.parameters){const v=node.parameters[p.id]??p.defaultValue;
  if(!Number.isFinite(v)||v<p.minValue||v>p.maxValue)throw new RangeError(`${node.id}.${p.id} outside range`);
  if(['enum','index','boolean'].includes(p.unit)&&!Number.isInteger(v))throw new RangeError(`${p.id} must be integer`);
  if(node.moduleId==='mir.mod-001'&&p.id==='frequency'&&v>=sr/2)throw new RangeError('frequency exceeds Nyquist');
  if(node.moduleId==='mir.mod-019'&&p.id==='cutoff-hz'&&v>sr*.49)throw new RangeError('cutoff exceeds sample-rate limit');
  parameters[p.id]=v;
 }
 return {id:node.id,moduleId:node.moduleId,parameters};
}
/** One reserve per admitted type plus actual initial closure needs, bounded TOTAL.
 * More capacity is an explicit Stop/rebuild, never a render-thread constructor.
 */
export function capacityForPlan(plan?:LivePlan):LiveCapacity {
 const capacity:Record<string,number>=Object.fromEntries(liveDefinitions.map(d=>[d.descriptor.id,1]));
 const counts:Record<string,number>={};
 for(const n of plan?.graph.nodes??[]){if(!definitions.has(n.moduleId))throw new RangeError('unsupported live module');counts[n.moduleId]=(counts[n.moduleId]??0)+1;}
 for(const [id,count]of Object.entries(counts))capacity[id]=Math.max(capacity[id]!,count);
 validateCapacity(capacity);return Object.freeze(capacity);
}
function validateCapacity(capacity:LiveCapacity){
 let total=0;
 for(const [id,count]of Object.entries(capacity)){
  if(!definitions.has(id)||!Number.isInteger(count)||count<0||count>LIVE_LIMITS.perModule)throw new RangeError('invalid capacity recipe');total+=count;
 }
 if(total>LIVE_LIMITS.nodes)throw new RangeError('capacity recipe exceeds 16 total slots');
}
/** Constructor is START preparation only, in a suspended live context; never call
 * it while this transport is audible. Metadata preparation is bounded but still
 * executes on the worklet thread; browser PCM edit-storm tests remain mandatory.
 */
export class LiveGraphEngine {
 private readonly pool:Slot[]=[];
 private active:Slot[]=[];private taps:Float32Array[]=[];
 private generation=0;private running=false;
 frames=0;
 get nodeCount(){return this.active.length;}
 get slotCount(){return this.pool.length;}
 constructor(readonly sampleRate:number,readonly capacity:LiveCapacity=capacityForPlan()){
  if(!LIVE_LIMITS.sampleRates.includes(sampleRate))throw new RangeError('live sampleRate must be 44100/48000/96000');
  validateCapacity(capacity);
  for(const definition of liveDefinitions)for(let n=0;n<(capacity[definition.descriptor.id]??0);n++){
   const outputs:Record<string,Float32Array>={};for(const p of definition.descriptor.outputs)outputs[p.id]=new Float32Array(128*p.channels);
   this.pool.push({definition,instance:definition.createInstance({sampleRate,maxBlockSize:128}),id:'',parameters:{},block:{frames:128,inputs:{},outputs,controls:{}},outputBuffers:Object.values(outputs),silence:new Float32Array(128)});
  }
 }
 /** No mutation of live slots, parameters, bindings or DSP history before ALL
  * graph/tap/capacity/parameter validation succeeds. Caller may keep this as the
  * sole pending revision and commit at a silent transition boundary.
  */
 prepare(plan:LivePlan):PreparedLivePlan {
  if(plan.sampleRate!==this.sampleRate||plan.graph.nodes.length>LIVE_LIMITS.nodes||plan.graph.connections.length>LIVE_LIMITS.connections||plan.graph.connections.some(c=>c.feedback))throw new RangeError('invalid live plan bounds/feedback');
  const order=validatePatchGraph(plan.graph,liveDefinitions.map(d=>d.descriptor)).executionOrder;
  if(plan.outputs.length<1||plan.outputs.length>2)throw new RangeError('select one mono or two stereo taps');
  const nodes=plan.graph.nodes.map(n=>normalizedNode(n,definitions.get(n.moduleId)!,this.sampleRate));
  // Validate every tap BEFORE any slot can be mutated.
  for(const tap of plan.outputs){const n=nodes.find(n=>n.id===tap.nodeId),p=definitions.get(n?.moduleId??'')?.descriptor.outputs.find(p=>p.id===tap.portId&&p.kind==='audio'&&p.channels===1);if(!p)throw new RangeError('invalid audio output');}
  const used=new Set<Slot>(),retained=new Map(nodes.map(n=>[n.id,this.active.find(s=>s.id===n.id&&s.definition.descriptor.id===n.moduleId)]));
  for(const s of retained.values())if(s)used.add(s);
  const bindings:Binding[]=nodes.map(node=>{
   const old=retained.get(node.id),slot=old??this.pool.find(s=>s.definition.descriptor.id===node.moduleId&&!used.has(s));
   if(!slot)throw new RangeError(`Live capacity exhausted for ${node.moduleId}; Stop and rebuild reserve`);used.add(slot);
   const inputs:Record<string,Float32Array>={},controls:Record<string,Float32Array>={};
   for(const p of slot.definition.descriptor.inputs){if(p.kind==='audio')inputs[p.id]=slot.silence;else if(p.id==='gate'||p.id==='reset')controls[p.id]=slot.silence;}
   return {slot,node,retained:!!old,block:{frames:128,inputs,controls,outputs:slot.block.outputs}};
  });
  const byId=new Map(bindings.map(b=>[b.node.id,b]));
  for(const edge of plan.graph.connections){const source=byId.get(edge.source.nodeId)!,target=byId.get(edge.target.nodeId)!;
   const p=target.slot.definition.descriptor.inputs.find(p=>p.id===edge.target.portId)!;
   (p.kind==='audio'?target.block.inputs:target.block.controls)[p.id]=source.block.outputs[edge.source.portId]!;
  }
  return {owner:this,generation:this.generation,bindings,active:order.map(id=>byId.get(id)!.slot),taps:plan.outputs.map(p=>byId.get(p.nodeId)!.block.outputs[p.portId]!)};
 }
 commit(prepared:PreparedLivePlan){
  if(prepared.owner!==this||prepared.generation!==this.generation)throw new RangeError('stale prepared live plan');
  // Domain checks are complete. No constructors, maps, graph validation or new
  // host buffers here. Module setter allocation audit is a separate admission gate.
  for(const b of prepared.bindings){const s=b.slot;
   for(const p of s.definition.descriptor.parameters)if(!b.retained||b.node.parameters[p.id]!==s.parameters[p.id])s.instance.setParameter(p.id,b.node.parameters[p.id]!);
   if(!b.retained)s.instance.reset();
   s.id=b.node.id;s.parameters=b.node.parameters;s.block=b.block;
  }
  this.active=prepared.active;this.taps=prepared.taps;this.running=true;this.generation++;
 }
 install(plan:LivePlan){this.commit(this.prepare(plan));}
 stop(){this.running=false;this.active=[];this.taps=[];this.generation++;}
 process(left:Float32Array,right:Float32Array){
  if(left.length!==128||right.length!==128)throw new RangeError('expected 128 frame quantum');
  left.fill(0);right.fill(0);if(!this.running)return;
  try{
   for(let n=0;n<this.active.length;n++){const node=this.active[n]!;
    for(let j=0;j<node.outputBuffers.length;j++)node.outputBuffers[j]!.fill(0);
    node.instance.process(node.block);
    for(let j=0;j<node.outputBuffers.length;j++)for(let i=0;i<node.outputBuffers[j]!.length;i++)if(!Number.isFinite(node.outputBuffers[j]![i]!))throw new RangeError('DSP produced non-finite output');
   }
   left.set(this.taps[0]!);right.set(this.taps[1]??this.taps[0]!);this.frames+=128;
  }catch(error){left.fill(0);right.fill(0);this.stop();throw error;}
 }
}
